import { useMemo } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type { WarehouseTreeNode } from '@/lib/types';
import { collectAncestorGroupIds, collectDescendantGroupIds } from '@/lib/utils/warehouse-tree';
import { useTreeView } from '@/hooks/useTreeView';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface StoreGroupExclusiveSelectProps {
  /** 倉庫ツリー（プレオーダー平坦リスト。配列順=表示順） */
  nodes: WarehouseTreeNode[];
  /** 選択中の店舗グループID */
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  /** チェックボックス id を一意にするための接頭辞（同一画面に複数配置するため） */
  idPrefix: string;
  /** ツリー読み込み中（「店舗グループがありません」との誤表示を防ぐ） */
  loading?: boolean;
}

/** 1階層ぶんのインデント幅(px) */
const INDENT_PX = 20;

/** チェックボックスをプロダクトの倉庫ツリーに合わせて角型にする（テーマの radius で丸くなるのを打ち消す） */
const SQUARE_CHECKBOX_CLASS = 'rounded-[2px]';

/**
 * 集計する店舗グループを選ぶツリー（FKV3-12858）
 *
 * 選んだグループがそのまま出力の1行になるため、選択値は末端の倉庫IDではなくグループIDで、
 * 親子は排他にする（親と子を同時に選ぶと、どちらの単位で丸めるかが決まらない）。
 * 末端の倉庫は出力単位になり得ないので表示しない。
 *
 * 親を選ぶと配下もチェック済みに見せる。行になるのは親だけだが、どこが対象範囲かを
 * ツリー上で読めるようにするため。配下のチェックを外すと親の選択が解ける
 * （「この枝はまとめない」という操作になる）。
 *
 * 倉庫そのものを選ぶツリーは `WarehouseMultiSelect`。
 */
export function StoreGroupExclusiveSelect({
  nodes,
  selectedIds,
  onChange,
  idPrefix,
  loading = false,
}: StoreGroupExclusiveSelectProps) {
  const viewNodes = useMemo(
    () =>
      nodes.map((n) => ({ name: n.name, level: n.level, isGroup: n.node_type === 'seller_group' })),
    [nodes],
  );
  const {
    search,
    setSearch,
    visibleIndexes,
    hiddenByCollapse,
    collapsedGroups,
    toggleCollapse,
    hasVisibleNodes,
  } = useTreeView(viewNodes, { groupsOnly: true });

  // 出力単位に選ばれたグループの配下（＝対象範囲）。チェック済みとして見せる。
  //
  // ノードごとに祖先を辿ると O(n^2) になるため、プレオーダーの性質を使って1パスで求める
  // （倉庫608件規模のアカウントでは選択のたびに数十万回の走査になってしまう）。
  const inRangeGroupIds = useMemo(() => {
    const inRange = new Set<string>();
    const selectedAncestorLevels: number[] = [];
    nodes.forEach((node) => {
      while (
        selectedAncestorLevels.length > 0 &&
        selectedAncestorLevels[selectedAncestorLevels.length - 1] >= node.level
      ) {
        selectedAncestorLevels.pop();
      }
      if (node.node_type !== 'seller_group') return;
      if (selectedAncestorLevels.length > 0) inRange.add(node.id);
      if (selectedIds.has(node.id)) selectedAncestorLevels.push(node.level);
    });
    return inRange;
  }, [nodes, selectedIds]);

  // 選択のトグル。
  // 入れるとき: 配下の選択を外して自身だけを出力単位にする（親子の同時選択を作らない）。
  //   祖先が選択されていれば自身は既にチェック済みなので、この経路には来ない。
  // 外すとき: 自身と祖先を落とす。親でまとめている枝の配下を外す操作は
  //   「この枝はまとめない」の意味になるため、親の選択も解く
  const toggle = (groupId: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) {
      collectDescendantGroupIds(nodes, groupId).forEach((id) => next.delete(id));
      next.add(groupId);
    } else {
      next.delete(groupId);
      collectAncestorGroupIds(nodes, groupId).forEach((id) => next.delete(id));
    }
    onChange(next);
  };

  const clearAll = () => onChange(new Set());

  if (loading) {
    return (
      <div className="flex items-center rounded-md border p-3 text-sm text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        読み込み中...
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="rounded-md border p-3 text-sm text-gray-500">店舗グループがありません</div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <Input
          placeholder="グループ名で検索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8"
        />
        {/* 「すべて選択」は出さない: 全階層を選ぶと親子が重なり、丸める単位が決まらなくなる */}
        <Button type="button" variant="outline" size="sm" onClick={clearAll}>
          すべて解除
        </Button>
      </div>
      <div className="text-xs text-gray-500">
        出力単位にする店舗グループ {selectedIds.size} 件（配下のチェックは対象範囲を示します）
      </div>
      <div className="max-h-56 space-y-1 overflow-y-auto">
        {!hasVisibleNodes ? (
          <div className="text-sm text-gray-500">該当する店舗グループがありません</div>
        ) : (
          nodes.map((node, index) => {
            if (node.node_type !== 'seller_group') return null;
            if (visibleIndexes !== null && !visibleIndexes.has(index)) return null;
            // 検索中は折りたたみを無視して一致結果を表示する（自動展開扱い）
            if (visibleIndexes === null && hiddenByCollapse.has(index)) return null;
            const collapsed = collapsedGroups.has(index);
            return (
              <div
                key={`sg-${node.id}-${index}`}
                className="flex items-center gap-2"
                style={{ paddingLeft: `${(node.level - 1) * INDENT_PX}px` }}
              >
                <button
                  type="button"
                  onClick={() => toggleCollapse(index)}
                  className="flex h-4 w-4 shrink-0 items-center justify-center text-gray-500 hover:text-gray-800"
                  aria-label={collapsed ? '展開' : '折りたたみ'}
                >
                  {collapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
                <Checkbox
                  id={`${idPrefix}-sg-${index}`}
                  className={SQUARE_CHECKBOX_CLASS}
                  checked={selectedIds.has(node.id) || inRangeGroupIds.has(node.id)}
                  onCheckedChange={(checked) => toggle(node.id, checked === true)}
                />
                <Label htmlFor={`${idPrefix}-sg-${index}`} className="font-medium text-gray-700">
                  {node.name}
                </Label>
                {/* 同じチェック済みでも「行になるのはどれか」が読めるようにする */}
                {selectedIds.has(node.id) && (
                  <span className="shrink-0 text-xs text-primary">出力単位</span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
